<template>
  <div class="modal-overlay">
    <div class="modal-container">
      <div class="modal-header">
        <h3 class="text-lg font-semibold">
          Message Preview
        </h3>
        <button
          class="close-button"
          @click="close">
          <i class="fas fa-times" />
        </button>
      </div>

      <div class="modal-body">
        <!-- Recipients -->
        <div class="preview-section">
          <label class="preview-label">To:</label>
          <div class="preview-recipients">
            <div
              v-for="recipient in previewData.recipients"
              :key="recipient.type"
              class="recipient-badge">
              <i class="fas fa-user mr-1" />
              {{ recipient.name }} &lt;{{ recipient.email }}&gt;
            </div>
          </div>
        </div>

        <!-- Subject -->
        <div class="preview-section">
          <label class="preview-label">Subject:</label>
          <p class="preview-subject">
            {{ previewData.subject }}
          </p>
        </div>

        <!-- Missing Variables Warning -->
        <div
          v-if="previewData.missing_variables && previewData.missing_variables.length > 0"
          class="warning-box">
          <i class="fas fa-exclamation-triangle mr-2" />
          <div>
            <p class="font-medium">
              Missing Information:
            </p>
            <ul class="text-sm mt-1">
              <li
                v-for="variable in previewData.missing_variables"
                :key="variable">
                {{ variable }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Body -->
        <div class="preview-section">
          <label class="preview-label">Message:</label>
          <div
            class="preview-body"
            v-html="previewData.body" />
        </div>

        <!-- Template Info -->
        <div class="preview-footer">
          <p class="text-xs text-gray-500">
            Template: {{ previewData.template_name }} ({{ previewData.event_type }})
          </p>
        </div>
      </div>

      <div class="modal-actions">
        <button
          class="btn btn-secondary"
          @click="close">
          Cancel
        </button>
        <button
          class="btn btn-primary"
          @click="confirmSend">
          <i class="fas fa-paper-plane mr-2" />
          Send Message
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  previewData: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'send'])

const close = () => {
  emit('close')
}

const confirmSend = () => {
  emit('send')
  close()
}
</script>

<style scoped>
.modal-overlay {
  @apply fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50;
}

.modal-container {
  @apply bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col;
}

.modal-header {
  @apply flex items-center justify-between px-6 py-4 border-b border-gray-200;
}

.close-button {
  @apply p-2 text-gray-400 hover:text-gray-600 transition-colors;
}

.modal-body {
  @apply flex-1 overflow-y-auto px-6 py-4 space-y-4;
}

.preview-section {
  @apply space-y-1;
}

.preview-label {
  @apply text-xs font-medium text-gray-500 uppercase tracking-wider;
}

.preview-recipients {
  @apply flex flex-wrap gap-2;
}

.recipient-badge {
  @apply inline-flex items-center px-3 py-1 bg-gray-100 rounded-full text-sm;
}

.preview-subject {
  @apply text-lg font-medium text-gray-900;
}

.warning-box {
  @apply bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start text-yellow-800;
}

.preview-body {
  @apply bg-white border border-gray-200 rounded-lg p-6 text-gray-700 leading-relaxed;
}

.preview-body :deep(p) {
  @apply mb-4 text-gray-700;
}

.preview-body :deep(ul),
.preview-body :deep(ol) {
  @apply ml-6 mb-4 text-gray-700;
}

.preview-body :deep(li) {
  @apply mb-2;
}

.preview-body :deep(strong) {
  @apply font-semibold text-gray-900;
}

.preview-body :deep(a) {
  @apply text-indigo-600 hover:text-indigo-700 underline;
}

.preview-body :deep(a.button) {
  @apply inline-block px-6 py-3 bg-indigo-600 text-white rounded-md no-underline font-medium hover:bg-indigo-700;
}

.preview-body :deep(.alert-box) {
  @apply bg-red-50 border border-red-200 border-l-4 border-l-red-500 p-4 my-4 rounded text-red-900;
}

.preview-body :deep(.warning-box) {
  @apply bg-yellow-50 border border-yellow-200 border-l-4 border-l-yellow-500 p-4 my-4 rounded text-yellow-900;
}

.preview-body :deep(.info-box) {
  @apply bg-blue-50 border border-blue-200 border-l-4 border-l-blue-500 p-4 my-4 rounded text-blue-900;
}

.preview-footer {
  @apply pt-2 border-t border-gray-200;
}

.modal-actions {
  @apply flex justify-end gap-3 px-6 py-4 border-t border-gray-200;
}

.btn {
  @apply inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg
         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2;
}

.btn-primary {
  @apply bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500;
}

.btn-secondary {
  @apply bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500;
}
</style>
